function publicKeyCredential () {
  if (typeof window === 'undefined') return null;
  return window.PublicKeyCredential || null;
}

function b64urlEncode (buf) {
  const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf;
  let str = '';
  bytes.forEach((b) => { str += String.fromCharCode(b); });
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function b64urlDecode (str) {
  const pad = '='.repeat((4 - (str.length % 4)) % 4);
  const bin = atob(str.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function unpaddedB64url (value) {
  if (typeof value !== 'string') return value;
  return value.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function decodeDescriptors (descriptors) {
  if (!Array.isArray(descriptors)) return descriptors;
  return descriptors.map((descriptor) => Object.assign({}, descriptor, {
    id: b64urlDecode(descriptor.id)
  }));
}

function creationOptionsFromJson (options) {
  const publicKey = Object.assign({}, options, {
    challenge: b64urlDecode(options.challenge),
    user: Object.assign({}, options.user, {
      id: b64urlDecode(options.user.id)
    }),
    pubKeyCredParams: [{ type: 'public-key', alg: -7 }],
    attestation: 'none',
    authenticatorSelection: Object.assign({}, options.authenticatorSelection, {
      userVerification: 'required'
    })
  });
  if (options.excludeCredentials) {
    publicKey.excludeCredentials = decodeDescriptors(options.excludeCredentials);
  }
  return publicKey;
}

function requestOptionsFromJson (options) {
  const publicKey = Object.assign({}, options, {
    challenge: b64urlDecode(options.challenge),
    userVerification: 'required'
  });
  if (options.allowCredentials) {
    publicKey.allowCredentials = decodeDescriptors(options.allowCredentials);
  } else {
    delete publicKey.allowCredentials;
  }
  return publicKey;
}

function manualCredentialToJson (cred) {
  const json = {
    id: cred.id,
    rawId: b64urlEncode(cred.rawId),
    type: cred.type,
    response: {}
  };
  const response = cred.response;
  if (response.clientDataJSON) json.response.clientDataJSON = b64urlEncode(response.clientDataJSON);
  if (response.attestationObject) json.response.attestationObject = b64urlEncode(response.attestationObject);
  if (response.authenticatorData) json.response.authenticatorData = b64urlEncode(response.authenticatorData);
  if (response.signature) json.response.signature = b64urlEncode(response.signature);
  if (response.userHandle) json.response.userHandle = b64urlEncode(response.userHandle);
  if (typeof response.getTransports === 'function') {
    json.response.transports = response.getTransports();
  }
  return json;
}

function credentialToJson (cred) {
  if (!cred || cred.type !== 'public-key') {
    throw new Error('Invalid passkey credential type');
  }

  const json = typeof cred.toJSON === 'function'
    ? cred.toJSON()
    : manualCredentialToJson(cred);
  const sourceResponse = json.response || {};
  const isRegistration = !!cred.response.attestationObject;
  const fields = isRegistration
    ? ['clientDataJSON', 'attestationObject']
    : ['clientDataJSON', 'authenticatorData', 'signature', 'userHandle'];
  const response = {};

  fields.forEach((field) => {
    if (sourceResponse[field] !== null && sourceResponse[field] !== undefined) {
      response[field] = unpaddedB64url(sourceResponse[field]);
    }
  });
  if (isRegistration && Array.isArray(sourceResponse.transports)) {
    response.transports = sourceResponse.transports;
  }

  return {
    id: unpaddedB64url(json.id || cred.id),
    rawId: unpaddedB64url(json.rawId || b64urlEncode(cred.rawId)),
    type: 'public-key',
    response
  };
}

function create (options) {
  if (!isSupported()) return Promise.reject(notSupportedError());
  return navigator.credentials.create({
    publicKey: creationOptionsFromJson(options)
  }).then(credentialToJson);
}

function get (options, mediation, signal) {
  if (!isSupported()) return Promise.reject(notSupportedError());
  const credentialOptions = {
    publicKey: requestOptionsFromJson(options),
    mediation: mediation || 'optional'
  };
  if (signal) credentialOptions.signal = signal;
  return navigator.credentials.get(credentialOptions).then(credentialToJson);
}

function isSupported () {
  return !!publicKeyCredential() &&
    window.isSecureContext !== false &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials;
}

function isConditionalMediationAvailable () {
  const credential = publicKeyCredential();
  if (!credential || typeof credential.isConditionalMediationAvailable !== 'function') {
    return Promise.resolve(false);
  }
  return credential.isConditionalMediationAvailable().catch(() => false);
}

function isPlatformAuthenticatorAvailable () {
  const credential = publicKeyCredential();
  if (!credential || typeof credential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') {
    return Promise.resolve(false);
  }
  return credential.isUserVerifyingPlatformAuthenticatorAvailable().catch(() => false);
}

function notSupportedError () {
  const error = new Error('Passkeys are not supported by this browser');
  error.name = 'NotSupportedError';
  return error;
}

function errorMessage (error, action) {
  const body = error && error.response && error.response.data;
  if (body && body.message) return body.message;
  if (error && error.message && !error.name) return error.message;
  if (error && error.name === 'NotAllowedError') return 'Passkey operation was cancelled.';
  if (error && error.name === 'InvalidStateError' && action === 'create') {
    return 'This authenticator is already registered.';
  }
  if (error && error.name === 'NotSupportedError') {
    return 'Passkeys are not supported by this browser or device.';
  }
  return action === 'create'
    ? 'Unable to register the passkey.'
    : 'Unable to sign in with a passkey.';
}

export default {
  b64urlEncode,
  b64urlDecode,
  credentialToJson,
  create,
  get,
  isSupported,
  isConditionalMediationAvailable,
  isPlatformAuthenticatorAvailable,
  errorMessage
}
